/*
    공통 모달  -  PPI , Kinase 워크플로우 성공 시, 보여주는 모달
*/

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Box,
  Typography,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'src/store/app/hooks';
import { setLibrary } from 'src/store/features/ppi/ppi-user-parameters/ppiUserParametersSlice';

// Props. 워크스페이스 상태와 라운드 이동에 필요한 목록 정보를 전달.
interface WorkspaceStatusModalProps {
  open: boolean;
  onClose: () => void;
  status: 'COMPLETED' | 'RUNNING' | 'FAILED' | 'not_started' | null;
  workspaces: string[];
  workflowType: string;
  roundType: string;
}

// 워크스페이스 상태 모달. 워크스페이스별 실행 상태와 이동 가능한 항목을 표시.
const WorkspaceStatusModal: React.FC<WorkspaceStatusModalProps> = ({
  open,
  onClose,
  status,
  workspaces,
  workflowType,
  roundType
}) => {


  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return '#10b981';
      case 'RUNNING':
        return '#f59e0b';
      case 'FAILED':
        return '#ef4444';
      case 'not_started':
        return '#64748b';
      default:
        return '#64748b';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return t('dashboard.completedProjects');
      case 'RUNNING':
        return t('dashboard.running');
      case 'FAILED':
        return t('dashboard.failed');
      case 'not_started':
        return t('dashboard.notStarted');
      default:
        return status;
    }
  };

  const handleWorkspaceClick = (workspaceName: string) => {

    // console.log(workspaceName,'이게 업데이트가 되는건가');
    dispatch(setLibrary(workspaceName));
    if(workflowType === 'PPI Inhibitor'){
        navigate(`/ppi-inhibitor/${workspaceName}/round1`);
    }else if(workflowType === 'Kinase Inhibitor'){
        navigate(`/kinase-inhibitor/${workspaceName}/round1`);
    }
    onClose();
  };

  if (!status) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '10px',
          padding: 1.5
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        paddingBottom: 4,
        borderBottom: '3px solid #e0e0e0'
      }}>
        <Box>
          <Typography variant="body1" component="div" sx={{ fontWeight: 600 }}>
            Work Space {getStatusLabel(status)} list
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {workflowType} - {roundType}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {workspaces.length === 0 ? (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 4,
              color: 'text.secondary'
            }}
          >
            <Typography variant="body1">
                No workspaces found with status "{getStatusLabel(status)}".
            </Typography>
          </Box>
        ) : (
          <List sx={{ pt: 0 , marginTop : 3}}>
            {workspaces?.map((workspace, index) => (
              <ListItem 
                key={index} 
                disablePadding
                sx={{ 
                  mb: 1,
                  '&:last-child': { mb: 0 }
                }}
              >
                <ListItemButton
                  onClick={() => handleWorkspaceClick(workspace)}
                  sx={{
                    borderRadius: '5px',
                    backgroundColor: '#fafafa',
                    padding: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#3b82f6',
                      backgroundColor: '#f0f9ff',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {workspace}
                        </Typography>
                        <Chip
                          label={getStatusLabel(status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(status),
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '11px'
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Click to move workspace
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkspaceStatusModal;
